import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { SeoSchema, BreadcrumbSchema, getWebPageSchema } from '@/components/base/SeoSchema';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const category = useMemo(() => categories.find(c => c.slug === activeSlug), [activeSlug]);

  const filteredComics = useMemo(() => {
    let result = activeSlug === 'all' ? [...comics] : comics.filter(c => c.category === activeSlug);
    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
    }
    return result;
  }, [activeSlug, sortBy]);

  const pageTitle = category ? `${category.name}韩国漫画 - 免费下拉式在线阅读 | 韩漫阅读` : '全部韩国漫画 - 免费下拉式在线阅读 | 韩漫阅读';
  const pageDesc = category
    ? `免费阅读${category.name}类韩国漫画，涵盖${category.description}。支持便捷的下拉式浏览，无删减完整版，畅享最佳阅读体验。`
    : '韩漫阅读漫画大全，涵盖恋爱、奇幻、动作、悬疑、日常、剧情等全部分类韩国漫画，免费无删减，下拉式流畅阅读体验。';

  return (
    <>
      <SeoSchema
        type="WebPage"
        data={getWebPageSchema(pageTitle, pageDesc, `https://hanmanyuedu.com/category/${activeSlug}`) as Record<string, unknown>}
      />
      <BreadcrumbSchema
        items={[
          { name: '首页', item: 'https://hanmanyuedu.com' },
          { name: category?.name || '全部漫画', item: `https://hanmanyuedu.com/category/${activeSlug}` },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '全部漫画' },
              ]}
            />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {category ? `${category.name}韩国漫画` : '韩国漫画大全'}
              </h1>
              <p className="text-sm text-foreground-500">
                {category ? `${category.description}，共 ${filteredComics.length} 部作品` : `共收录 ${filteredComics.length} 部韩国漫画作品，免费无删减阅读`}
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              <aside className="lg:w-48 flex-shrink-0">
                <div className="lg:sticky lg:top-20">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-3">漫画分类</h3>
                  <nav className="space-y-0.5">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`block px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                          activeSlug === cat.slug
                            ? 'bg-primary-500 text-background-50 font-medium'
                            : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950'
                        }`}
                      >
                        {cat.name}
                        {cat.slug !== 'all' && (
                          <span className="ml-1.5 text-xs opacity-70">
                            ({comics.filter(c => c.category === cat.slug).length})
                          </span>
                        )}
                      </Link>
                    ))}
                  </nav>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                  <p className="text-sm text-foreground-500">
                    共 <strong className="text-foreground-900">{filteredComics.length}</strong> 部作品
                  </p>
                  <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                    {([
                      { value: 'latest', label: '最新' },
                      { value: 'popular', label: '人气' },
                      { value: 'rating', label: '评分' },
                    ] as const).map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setSortBy(opt.value)}
                        className={`px-3 py-1.5 text-xs rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                          sortBy === opt.value
                            ? 'bg-primary-500 text-background-50 font-medium'
                            : 'text-foreground-600 hover:text-foreground-950'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {filteredComics.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                    {filteredComics.map((comic) => (
                      <ComicCard key={comic.id} comic={comic} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-300">
                      <i className="ri-emotion-sad-line text-4xl"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无漫画作品</p>
                    <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 mt-2 inline-block">
                      查看全部漫画
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}